import React from "react";
import type { Lot } from "@bloomberg/octagon-shared";
import { DetailCard, IconButton } from "@bloomberg/media.atomics";
import { Add, Edit } from "@bloomberg/media.icons";
import styles from "./holdingsDrawer.module.css";
import HoldingsRow from "~components/Watchlist/delta/BulkEdit/HoldingsRow";
import { formatDisplayDate } from "~utils/dateTimeUtils";
import { formatWithCommasAndDecimals } from "~utils/portfolio/formatNumber";

export type EditFormState = {
  readonly date: string;
  readonly shares: string;
  readonly price: string;
};

type AddRowActiveProps = {
  readonly isAdding: true;
  readonly addForm: EditFormState;
  readonly onAddFieldChange: (
    field: "date" | "shares" | "price",
    value: string
  ) => void;
  readonly onRemoveAddRow: () => void;
};

type AddRowInactiveProps = {
  readonly isAdding?: false;
  readonly addForm?: never;
  readonly onAddFieldChange?: never;
  readonly onRemoveAddRow?: never;
};

type AddRowProps = AddRowActiveProps | AddRowInactiveProps;

type DrawerHistoryTableProps = {
  readonly lots: ReadonlyArray<Lot>;
  readonly editingLotId: number | null;
  readonly editForm: EditFormState;
  readonly onEditStart: (lotId: number) => void;
  readonly onFieldChange: (
    field: "date" | "shares" | "price",
    value: string
  ) => void;
  readonly onDeleteLot: () => void;
  readonly onValidationChange?: (hasErrors: boolean) => void;
  readonly hasNoLots?: boolean;
  readonly onAddLot?: () => void;
} & AddRowProps;

export default function DrawerHistoryTable({
  lots,
  editingLotId,
  editForm,
  onEditStart,
  onFieldChange,
  onDeleteLot,
  onValidationChange,
  hasNoLots,
  onAddLot,
  isAdding,
  addForm,
  onAddFieldChange,
  onRemoveAddRow,
}: DrawerHistoryTableProps) {
  return (
    <section>
      <h3 className={styles.sectionTitle}>History</h3>
      <DetailCard
        className={styles.historyCard}
        orientation="vertical"
        header={
          <div className={styles.historyHeaders}>
            <span className={styles.historyHeaderLabel}>Date</span>
            <span className={styles.historyHeaderLabel}>Shares</span>
            <span className={styles.historyHeaderLabel}>Price</span>
          </div>
        }
        body={
          hasNoLots && !isAdding ? (
            <div className={styles.noLotsState}>
              <p className={styles.noLotsMessage}>
                Add lots to view your total watchlist value.
              </p>
              <button
                type="button"
                className={styles.noLotsAddBtn}
                onClick={onAddLot}
              >
                <Add aria-hidden="true" />
                Add Lot
              </button>
            </div>
          ) : (
            <>
              {lots.map((lot) =>
                lot.id === editingLotId ? (
                  <div key={lot.id} className={styles.historyRowEditing}>
                    <HoldingsRow
                      holding={{
                        id: String(editingLotId),
                        date: editForm.date,
                        shares: editForm.shares,
                        price: editForm.price,
                      }}
                      onDateChange={(_, v) => onFieldChange("date", v)}
                      onSharesChange={(_, v) => onFieldChange("shares", v)}
                      onPriceChange={(_, v) => onFieldChange("price", v)}
                      onRemove={onDeleteLot}
                      disableRemove={false}
                      onValidationChange={onValidationChange}
                    />
                  </div>
                ) : (
                  <div key={lot.id} className={styles.historyRow} role="row">
                    <span className={styles.historyCell}>
                      {formatDisplayDate(lot.buyDate)}
                    </span>
                    <span className={styles.historyCell}>{lot.shares}</span>
                    <span className={styles.historyCell}>
                      {formatWithCommasAndDecimals(lot.buyPrice)}
                    </span>
                    <IconButton
                      className={styles.editBtn}
                      size="extra-small"
                      variant="ghost"
                      label="Edit holding"
                      onClick={() => onEditStart(lot.id)}
                    >
                      <Edit />
                    </IconButton>
                  </div>
                )
              )}
              {isAdding && (
                <div className={styles.historyRowEditing}>
                  <HoldingsRow
                    holding={{
                      id: "new",
                      date: addForm.date,
                      shares: addForm.shares,
                      price: addForm.price,
                    }}
                    onDateChange={(_, v) => onAddFieldChange("date", v)}
                    onSharesChange={(_, v) => onAddFieldChange("shares", v)}
                    onPriceChange={(_, v) => onAddFieldChange("price", v)}
                    onRemove={onRemoveAddRow}
                    disableRemove={false}
                    onValidationChange={onValidationChange}
                  />
                </div>
              )}
            </>
          )
        }
      />
    </section>
  );
}
